// Config-driven number-base and text-encoding converter engine. Powers every
// /tools/<x>-to-<y> base/encoding page from one component: each variant just
// declares its source and target representation. All conversion is local.

export type BaseConverterVariant =
  | 'binary-to-decimal'
  | 'decimal-to-binary'
  | 'binary-to-hex'
  | 'hex-to-binary'
  | 'decimal-to-hex'
  | 'hex-to-decimal'
  | 'binary-to-octal'
  | 'octal-to-binary'
  | 'decimal-to-octal'
  | 'octal-to-decimal'
  | 'hex-to-octal'
  | 'octal-to-hex'
  | 'text-to-binary'
  | 'binary-to-text'
  | 'text-to-hex'
  | 'hex-to-text'
  | 'text-to-ascii'
  | 'ascii-to-text'
  | 'text-to-decimal'
  | 'decimal-to-text'
  | 'text-to-octal'
  | 'octal-to-text'
  | 'ascii-to-binary'
  | 'binary-to-ascii';

type Radix = 2 | 8 | 10 | 16;

type NumberConfig = {
  mode: 'number';
  from: Radix;
  to: Radix;
  fromLabel: string;
  toLabel: string;
  sample: string;
};

type TextConfig = {
  mode: 'text';
  kind: 'binary' | 'hex' | 'ascii' | 'octal';
  direction: 'encode' | 'decode';
  fromLabel: string;
  toLabel: string;
  sample: string;
};

export type BaseConverterConfig = NumberConfig | TextConfig;

const radixName: Record<Radix, string> = { 2: 'Binary', 8: 'Octal', 10: 'Decimal', 16: 'Hex' };

function num(from: Radix, to: Radix, sample: string): NumberConfig {
  return { mode: 'number', from, to, fromLabel: radixName[from], toLabel: radixName[to], sample };
}

function text(
  kind: TextConfig['kind'],
  direction: TextConfig['direction'],
  sample: string,
): TextConfig {
  const encoded = { binary: 'Binary', hex: 'Hex', ascii: 'ASCII codes', octal: 'Octal' }[kind];
  return {
    mode: 'text',
    kind,
    direction,
    fromLabel: direction === 'encode' ? 'Text' : encoded,
    toLabel: direction === 'encode' ? encoded : 'Text',
    sample,
  };
}

export const baseConverterConfigs: Record<BaseConverterVariant, BaseConverterConfig> = {
  'binary-to-decimal': num(2, 10, '1010'),
  'decimal-to-binary': num(10, 2, '10'),
  'binary-to-hex': num(2, 16, '11110000'),
  'hex-to-binary': num(16, 2, 'f0'),
  'decimal-to-hex': num(10, 16, '255'),
  'hex-to-decimal': num(16, 10, 'ff'),
  'binary-to-octal': num(2, 8, '111000'),
  'octal-to-binary': num(8, 2, '70'),
  'decimal-to-octal': num(10, 8, '64'),
  'octal-to-decimal': num(8, 10, '100'),
  'hex-to-octal': num(16, 8, 'ff'),
  'octal-to-hex': num(8, 16, '777'),
  'text-to-binary': text('binary', 'encode', 'Hello'),
  'binary-to-text': text('binary', 'decode', '01001000 01101001'),
  'text-to-hex': text('hex', 'encode', 'Hello'),
  'hex-to-text': text('hex', 'decode', '48 69'),
  'text-to-ascii': text('ascii', 'encode', 'Hello'),
  'ascii-to-text': text('ascii', 'decode', '72 105'),
  'text-to-decimal': text('ascii', 'encode', 'Hello'),
  'decimal-to-text': text('ascii', 'decode', '72 105'),
  'text-to-octal': text('octal', 'encode', 'Hello'),
  'octal-to-text': text('octal', 'decode', '110 151'),
  'ascii-to-binary': text('binary', 'encode', 'Hello'),
  'binary-to-ascii': text('binary', 'decode', '01001000 01101001'),
};

export function getBaseConverterConfig(variant: BaseConverterVariant): BaseConverterConfig {
  return baseConverterConfigs[variant];
}

const digitToValue = (ch: string): number => {
  const code = ch.toLowerCase().charCodeAt(0);
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 97 && code <= 122) return code - 97 + 10;
  return Number.NaN;
};

function parseInteger(raw: string, radix: Radix): bigint {
  const trimmed = raw.trim();
  if (!trimmed) throw new Error('Enter a number to convert.');
  const negative = trimmed.startsWith('-');
  const body = (negative ? trimmed.slice(1) : trimmed).replace(/^0[bxo]/i, '');
  if (!body) throw new Error('Enter a valid number to convert.');
  let value = BigInt(0);
  const base = BigInt(radix);
  for (const ch of body) {
    const digit = digitToValue(ch);
    if (Number.isNaN(digit) || digit >= radix) {
      throw new Error(`"${ch}" is not a valid ${radixName[radix].toLowerCase()} digit.`);
    }
    value = value * base + BigInt(digit);
  }
  return negative ? -value : value;
}

// Convert one or many whitespace/comma separated numbers so batch pastes work.
function convertNumberField(raw: string, from: Radix, to: Radix): string {
  const tokens = raw
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);
  if (!tokens.length) throw new Error('Enter a number to convert.');
  return tokens
    .map((token) => {
      const value = parseInteger(token, from);
      const negative = value < BigInt(0);
      const text = (negative ? -value : value).toString(to);
      const upper = to === 16 ? text.toUpperCase() : text;
      return negative ? `-${upper}` : upper;
    })
    .join(' ');
}

function encodeText(input: string, kind: TextConfig['kind']): string {
  const bytes = Array.from(new TextEncoder().encode(input));
  if (!bytes.length) throw new Error('Enter some text to convert.');
  return bytes
    .map((byte) => {
      if (kind === 'binary') return byte.toString(2).padStart(8, '0');
      if (kind === 'hex') return byte.toString(16).padStart(2, '0');
      if (kind === 'octal') return byte.toString(8).padStart(3, '0');
      return String(byte);
    })
    .join(' ');
}

function decodeText(input: string, kind: TextConfig['kind']): string {
  const cleaned = input.trim();
  if (!cleaned) throw new Error('Enter values to convert back to text.');
  let bytes: number[];
  if (kind === 'binary') {
    const digits = cleaned.replace(/\s+/g, '');
    // A byte is 8 bits. A stray or missing bit used to be absorbed into a short
    // trailing group, which silently shifted every character after it.
    if (digits.length % 8 !== 0) {
      throw new Error(
        `Binary text needs a whole number of 8-bit bytes. You have ${digits.length} bits, which is ${digits.length % 8} too many.`,
      );
    }
    const groups = digits.match(/.{1,8}/g) ?? [];
    bytes = groups.map((group) => {
      if (!/^[01]+$/.test(group)) throw new Error('Binary input must contain only 0 and 1.');
      return Number.parseInt(group, 2);
    });
  } else if (kind === 'hex') {
    const hex = cleaned.replace(/0x/gi, '').replace(/[\s,]+/g, '');
    if (!/^[0-9a-f]*$/i.test(hex) || hex.length % 2 !== 0) {
      throw new Error('Hex input must be pairs of 0-9 and a-f.');
    }
    bytes = (hex.match(/.{2}/g) ?? []).map((pair) => Number.parseInt(pair, 16));
  } else if (kind === 'octal') {
    bytes = cleaned
      .split(/[\s,]+/)
      .filter(Boolean)
      .map((token) => {
        if (!/^[0-7]+$/.test(token)) throw new Error('Octal input must contain only digits 0-7.');
        const value = Number.parseInt(token, 8);
        if (value > 255) throw new Error('Each octal value must be 0 to 377 (a single byte).');
        return value;
      });
  } else {
    bytes = cleaned
      .split(/[\s,]+/)
      .filter(Boolean)
      .map((token) => {
        const value = Number(token);
        if (!Number.isInteger(value) || value < 0 || value > 255) {
          throw new Error('ASCII codes must be whole numbers from 0 to 255.');
        }
        return value;
      });
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
}

export function convertBase(config: BaseConverterConfig, input: string): string {
  if (config.mode === 'number') {
    return convertNumberField(input, config.from, config.to);
  }
  return config.direction === 'encode'
    ? encodeText(input, config.kind)
    : decodeText(input, config.kind);
}
